import { useMemo } from 'react';

import { NestedSelectProps } from '~/core/shared/components';
import { EMPTY_ARR } from '~/core/shared/constants';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { PATH_SEPARATOR } from '../../constants/separator';
import { getNestedOptionLabel } from '../../logic/getNestedOptionLabel';
import { buildNestedOptionPath } from './logic/buildNestedOptionPath';
import { getNestedSelectLabels } from './logic/getNestedSelectLabels';
import { getNestedSelectOptions } from './logic/getNestedSelectOptions';
import { isNestedSelectValueUnresolved } from './logic/isNestedSelectValueUnresolved';
import { toSelectOptions } from './logic/toSelectOptions';

type UseNestedSelectArgs = Pick<NestedSelectProps, 'fieldLabel' | 'options'> & {
  onChange: (path: string) => void;
  value: unknown;
};

export const useNestedSelect = ({
  fieldLabel,
  onChange,
  options,
  value,
}: UseNestedSelectArgs) => {
  const path = typeof value === 'string' ? value : null;

  const segments = useMemo(
    () => (path ? path.split(PATH_SEPARATOR) : EMPTY_ARR),
    [path],
  );

  const { selects, labels, unresolved } = useMemo(() => {
    const nestedSelects = getNestedSelectOptions(options, segments);

    return {
      selects: nestedSelects.map(toSelectOptions),
      labels: getNestedSelectLabels(nestedSelects, segments, fieldLabel),
      unresolved: isNestedSelectValueUnresolved(nestedSelects, segments),
    };
  }, [fieldLabel, options, segments]);

  const pathLabel = useMemo(
    () => getNestedOptionLabel(options, path) ?? path,
    [options, path],
  );

  const onSelectChange = useEventHandler((index: number, next: string) => {
    onChange(buildNestedOptionPath(path, index, next || null) ?? '');
  });

  return {
    labels,
    onSelectChange,
    path,
    pathLabel,
    segments,
    selects,
    unresolved,
  };
};
